/**
 * Interim sign-in. **Not designed yet** (docs/design/open-questions.md #5) —
 * built from Reef's InputField/Button so the live tree can be exercised
 * against a real API; replace with the designed screen when it lands.
 */
import { Button, InputField, Logomark, Text } from '@deepdive-ui';
import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router';

import { useAppNavigate } from '@/app/router/useAppNavigate';

import { testRoutesEnabled } from '@/data-sources/load';
import { authApi, LoginRequest } from '@/features/auth/auth.api';
import { tokenStorage } from '@/services/tokenStorage';
import { api } from '@/store/api/api';
import { useAppDispatch } from '@/store/hooks';
import { resetFilters } from '@/store/slices/filtersSlice';

import styles from './Login.module.css';

export default function LoginPage() {
  const navigate = useAppNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [login, { isLoading, error }] = authApi.useLoginMutation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'email' | 'password', string>>>({});

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = LoginRequest.safeParse({ email, password });
    if (!parsed.success) {
      const errors: typeof fieldErrors = {};
      for (const issue of parsed.error.issues)
        errors[issue.path[0] as 'email' | 'password'] = issue.message;
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    const result = await login(parsed.data);
    if ('data' in result && result.data) {
      tokenStorage.setTokens(result.data.accessToken, result.data.refreshToken);
      // This may be another account: drop the previous session's cached data and chips.
      dispatch(api.util.resetApiState());
      dispatch(resetFilters({ source: 'live' }));
      const from = (location.state as { from?: string } | null)?.from ?? '/';
      navigate(from, { replace: true });
    }
  };

  return (
    <div className={styles.page}>
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <div className={styles.panel}>
          <Logomark />
          <Text as="h1" size="md" weight="semibold" color="primary">
            Sign in to DeepDive
          </Text>
          <div className={styles.fields}>
            <InputField
              label="Email"
              name="email"
              inputMode="email"
              value={email}
              onValueChange={setEmail}
              errorMessage={fieldErrors.email}
            />
            <InputField
              label="Password"
              name="password"
              secure
              value={password}
              onValueChange={setPassword}
              errorMessage={fieldErrors.password}
            />
            {error && (
              <p className={styles.error} role="alert">
                {'message' in error ? String(error.message) : 'Sign in failed.'}
              </p>
            )}
            <Button hierarchy="primary" type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in…' : 'Sign in'}
            </Button>
            {testRoutesEnabled && (
              <Button hierarchy="ghostColor" href="/test">
                Open the dashboard with sample data
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
